import React, { useEffect, useState, useCallback } from "react";
import { LuUser } from "react-icons/lu";
import { TbCalendarTime } from "react-icons/tb";
import { getStatusBgColor, statusIcons } from "../../Enum/LeadEnumConstants";
import { followUpsStages } from "../../Enum/FollowUpsEnumConstants";
import { useNavigate } from "react-router-dom";
import { teamIds, userRole, userUid, teamDetails } from "../../utils/storage";
import DashedCircleLoader from "../../components/pipelineComponents/DashedCircleLoader";
import { getIcons } from "../../components/Constant/Constant";
import { formatDate, formatDateValue } from "../../utils/dateFormatter";
import SittingService from "../Service/SittingService";
import { roleIdProcurement } from "../../GlobalSetupComponents/Enum/RoleEnum";
import { LocationEditIcon } from "lucide-react";
import { IoLocationOutline } from "react-icons/io5";

const OverallSittingList = ({
  activeTab,
  startDate,
  endDate,
  selectedTeam,
  setSelectedTeam,
  selectedUser,
  setSelectedUser,
  refreshTrigger,
  onSittingFormOpen,
}) => {
  const [teamList, setTeamList] = useState([]);
  const [overAllCount, setOverAllCount] = useState({});
  const [selectedId, setSelectedId] = useState(null);
  const icons = getIcons();
  const navigate = useNavigate();

  const [stageData, setStageData] = useState({
    upcoming: { data: [], count: 0, next: "", loading: false, hasMore: true },
    noUpdate: { data: [], count: 0, next: "", loading: false, hasMore: true },
    completed: { data: [], count: 0, next: "", loading: false, hasMore: true },
    cancelled: { data: [], count: 0, next: "", loading: false, hasMore: true },
  });

  const getTeamList = async () => {
    try {
      const formattedStartDate = (startDate);
      const formattedEndDate = (endDate);

      const body = {
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        roleRefs: roleIdProcurement,
      };
      const response = await SittingService.getSittingOverallCount(body);
      const data = response.data?.data || {};
      setTeamList(data.userCounts || []);
      setOverAllCount(data.overAllCount || {});
    } catch (error) {
      console.error("Error fetching team list:", error);
    }
  };

  useEffect(() => {
    getTeamList();
  }, [startDate, endDate, selectedTeam, selectedUser]);

  const handleSittingItemClick = (item) => {
    const sittingData = {
      id: item.id,
      sittingTitle: item.taskName || "",
      sittingSpot: item.location || "",
      primarySittingPerson: item.userName
        ? { label: item.userName, value: item.userName }
        : null,
      secondarySittingPerson: item.secondaryUserName
        ? { label: item.secondaryUserName, value: item.secondaryUserName }
        : null,
      meetingPersonType: "",
      meetingPersonName: item.name || "",
      sittingDate: item.date || "",
      otherNotes: item.notes || "",
      status: item.status || "",
      propertyId: item.propertyId || "",
    };
    onSittingFormOpen(sittingData);
  };

  const sittingStatus = [
    { label: "UpComing", value: "upcoming" },
    { label: "No Update", value: "noUpdate" },
    { label: "Completed", value: "completed" },
    { label: "Cancelled", value: "cancelled" },
  ];

  const fetchStageData = async (stageValue, nextToken = "", isLoadMore = false) => {
    try {
      console.log(`Fetching stage data for: ${stageValue}`);
      
      setStageData((prev) => ({
        ...prev,
        [stageValue]: { ...prev[stageValue], loading: true },
      }));

      const formattedStartDate = startDate ? formatDateValue(startDate) : null;
      const formattedEndDate = endDate ? formatDateValue(endDate) : null;

      const body = {
        status: stageValue,
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        limit: 10,
        startAfter: nextToken || null,
        userId: selectedTeam || null,
      //  userId: selectedUser || null,
      };

      console.log('Request body:', body);

      const response = await SittingService.getSittingOverall(body);

      
      const { sittings, pagination , totalCount } = response.data?.data || { sittings: response.data?.data?.sittings || [], pagination: response.data?.data?.pagination || {} };



    
      const transformedData = (sittings || []).map((sitting) => ({
        id: sitting.sittingId,
        name: sitting.sittingPersons?.primary?.name || "N/A",
        taskName: sitting.title || "N/A",
        userName: sitting.sittingPersons?.primary?.name || "N/A",
        secondaryUserName: sitting.sittingPersons?.secondary?.name || "",
        location : sitting.location || "N/A",
        date: sitting.dateTime?.date || "N/A",
        time: sitting.dateTime?.time || "N/A",
        location: sitting.location || "N/A",
        status: stageValue, 
        notes: sitting.otherNotes || "",
        propertyId: sitting.propertyInfo?.id || "",
        propertyName: sitting.propertyInfo?.name || "",
        propertyLocation: sitting.propertyInfo?.location || "",
        overallArea: sitting.propertyInfo?.overallArea || 0,
      }));

      console.log(`Transformed data for ${stageValue}:`, transformedData);

      setStageData((prev) => ({
        ...prev,
        [stageValue]: {
          data: isLoadMore
            ? [...prev[stageValue].data, ...transformedData]
            : transformedData,
          count: totalCount || 0,
          next: pagination?.lastDocId || "",
          loading: false,
          hasMore: pagination?.hasMore || false,
        },
      }));
    } catch (error) {
      console.error(`Error fetching ${stageValue}:`, error);
      setStageData((prev) => ({
        ...prev,
        [stageValue]: { ...prev[stageValue], loading: false },
      }));
    }
  };

  const loadAllStages = async () => {
    const promises = sittingStatus.map((stage) => fetchStageData(stage.value));
    await Promise.all(promises);
  };

  const handleScroll = useCallback(
    (e, stageValue) => {
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      if (scrollHeight - scrollTop <= clientHeight + 100 && stageData[stageValue].hasMore && !stageData[stageValue].loading) {
        fetchStageData(stageValue, stageData[stageValue].next, true);
      }
    },
    [stageData]
  );

  useEffect(() => {
    setStageData({
      upcoming: { data: [], count: 0, next: "", loading: false, hasMore: true },
      noUpdate: { data: [], count: 0, next: "", loading: false, hasMore: true },
      completed: { data: [], count: 0, next: "", loading: false, hasMore: true },
      cancelled: { data: [], count: 0, next: "", loading: false, hasMore: true },
    });
    loadAllStages();
  }, [selectedTeam, startDate, endDate, selectedUser, refreshTrigger]);

  const handleTeamSelection = (id) => {
    if (selectedId === id) {
      setSelectedId(null);
      setSelectedTeam(null);
      setSelectedUser(null);
    } else {
      setSelectedId(id);
      setSelectedTeam(id);
      setSelectedUser(null);
    }
  };

  const handleAllSelection = () => {
    setSelectedId(null);
    setSelectedTeam(null);
    setSelectedUser(null);
  };

  const renderStageColumn = (stageKey, title, bgColor, borderColor, iconColor, stageColor) => {
    const stage = stageData[stageKey];



    return (
      <div className="flex flex-col gap-4">
        <div className="text-[24px] font-medium">
          {title} ({stage.count})
        </div>
        <div
          className={`${stageColor} p-3 h-[calc(100vh-270px)] overflow-y-auto w-[362px] rounded-[10px] shadow`}
          onScroll={(e) => handleScroll(e, stageKey)}
        >
          {stage.data.map((item) => (
            <div
              key={item.id}
              onClick={() => handleSittingItemClick(item)}
              className={`flex mb-4 cursor-pointer items-start gap-4 bg-white ${borderColor} rounded-[10px] px-4 py-4 hover:shadow-md transition-shadow`}
            >
              <div className="flex flex-col w-full">
                <div className="flex justify-between items-center mb-1">
                  <div
                    title={item.name}
                    className="text-base truncate w-[200px] font-medium"
                  >
                    {item.taskName || "N/A"}
                  </div>
                </div>
                <div className="flex flex-col gap-2 mt-2 text-xs text-gray-600">
                  <div className="flex items-center gap-2">
                    <LuUser className={`${iconColor} text-xl`} />
                   <div className="flex flex-col">
                   <div>{item.userName}</div>
                    {item.secondaryUserName && (
                   
                  
                   <div>{item.secondaryUserName}</div>
               
               )}
                   </div>
                  </div>
                
                  <div className="flex items-center gap-2">
                    <TbCalendarTime className={`${iconColor} text-xl`} />
                    <div>{item.date} {item.time}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <IoLocationOutline className={`${iconColor} text-xl`} />
                    {item.location}
                  </div>
                </div>
                <div className={`${stageColor} p-3 rounded-[10px] mt-4`}>
                  <div className={`${iconColor} text-[14px] font-semibold`}>
                    Property Info
                  </div>
                  <div className="flex text-[13px] items-center gap-3 flex-wrap mt-2">
                    <div>{item.propertyName || "N/A"}</div>
                    <div className="w-px h-4 bg-white" />
                    <div>Sq.ft {item.overallArea }</div>
                    <div className="w-px h-4 bg-white" />
                    <div>{item.propertyLocation || "N/A"}</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
          {stage.data.length === 0 && !stage.loading && (
            <div className="flex flex-col items-center justify-center h-full">
              <img
                src={icons.emptyFollowUp}
                alt="No Data"
                className="w-40 h-40"
              />
              <div className="text-gray-500 text-lg mt-4">
                {title === "UpComing"
                  ? "No Upcoming Sittings Available"
                  : title === "No Update"
                  ? "No Updates Available"
                  : title === "Completed"
                  ? "No Completed Sittings Available"
                  : "No Cancelled Sittings Available"}
              </div>
            </div>
          )}
          {stage.loading && (
            <div className="flex justify-center py-4">
              <DashedCircleLoader />
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderTeamSelectionColumn = () => {
    return (
      <div className="flex flex-col gap-4">
        <div className="text-[24px] font-medium">Select User</div>
        <div className="bg-white relative h-[calc(100vh-270px)] overflow-y-auto w-[370px] rounded-[10px] shadow">
          {overAllCount && (
            <div
              onClick={handleAllSelection}
              className={`p-3 rounded-lg mx-3 mt-3 top-0 sticky flex items-center justify-between mb-4 cursor-pointer transition-all duration-200 ${
                selectedId === null
                  ? "bg-blue-50 border-2 border-blue-200 shadow-sm"
                  : "bg-gray-50 border border-gray-200 hover:bg-gray-100"
              }`}
            >
              <div className="text-base font-medium cursor-pointer truncate w-40">
                All Users
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center rounded-full bg-blue-50 border border-blue-200 h-8 w-8 text-center justify-center text-blue-600 text-sm font-medium">
                  {overAllCount.upcoming || 0}
                </div> 
                <div className="flex items-center rounded-full bg-orange-50 border border-orange-200 h-8 w-8 text-center justify-center text-orange-600 text-sm font-medium">
                  {overAllCount.noUpdate || 0}
                </div>
                <div className="flex items-center rounded-full bg-green-50 border border-green-200 h-8 w-8 text-center justify-center text-green-600 text-sm font-medium">
                  {overAllCount.completed || 0}
                </div>
                <div className="flex items-center rounded-full bg-red-50 border border-red-200 h-8 w-8 text-center justify-center text-red-600 text-sm font-medium">
                  {overAllCount.cancelled || 0}
                </div>
              </div>
            </div>
          )}
          {teamList.map((item) => (
            <div
              key={item.id}
              onClick={() => handleTeamSelection(item.id)}
              className={`p-3 rounded-lg m-3 flex items-center justify-between mb-4 cursor-pointer transition-all duration-200 ${
                selectedId === item.id
                  ? "bg-blue-50 border-2 border-blue-200 shadow-sm"
                  : "bg-gray-50 border border-gray-200 hover:bg-gray-100"
              }`}
            >
              <div className="text-base font-medium cursor-pointer truncate w-40">
                {item.name}
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center rounded-full bg-blue-50 border border-blue-200 h-8 w-8 text-center justify-center text-blue-600 text-sm font-medium">
                  {item.counts?.upcoming || 0}
                </div>       
                <div className="flex items-center rounded-full bg-orange-50 border border-orange-200 h-8 w-8 text-center justify-center text-orange-600 text-sm font-medium">
                  {item.counts?.noUpdate || 0}
                </div>
                <div className="flex items-center rounded-full bg-green-50 border border-green-200 h-8 w-8 text-center justify-center text-green-600 text-sm font-medium">
                  {item.counts?.completed || 0}
                </div>
                <div className="flex items-center rounded-full bg-red-50 border border-red-200 h-8 w-8 text-center justify-center text-red-600 text-sm font-medium">
                  {item.counts?.cancelled || 0}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="h-screen overflow-x-auto pb-4 custom-scrollbar pipeline flex gap-6">
      {renderTeamSelectionColumn()}
      {renderStageColumn(
        "upcoming",
        "UpComing",
        "bg-blue-50",
        "border border-blue-200",
        "text-blue-600",
        "bg-[#F3FAFF]"
      )}
      {renderStageColumn(
        "noUpdate",
        "No Update",
        "bg-orange-50",
        "border border-orange-200",
        "text-orange-600",
        "bg-[#FEEEE5]"
      )}
      {renderStageColumn(
        "completed",
        "Completed",
        "bg-green-50",
        "border border-green-200",
        "text-green-600",
        "bg-[#F3FFF7]"
      )}
      {renderStageColumn(
        "cancelled",
        "Cancelled",
        "bg-red-50",
        "border border-red-200",
        "text-red-600",
        "bg-[#FFF3F3]"
      )}
    </div>
  );
};

export default OverallSittingList;